package com.vizio.pay.api

import com.vizio.redwolf.pay.api.SubscriptionService
import com.vizio.redwolf.pay.model.PartnerPlansInfo
import com.vizio.redwolf.pay.model.PreviewSubscriptionInfo
import com.vizio.redwolf.pay.model.SubscriptionResult
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import kotlin.js.Promise

@JsExport
class SubscriptionJsApi(payEnv: PayEnvironment, jsClientConfig: JsClientConfig? = null) {
    private val subscriptionService = SubscriptionService(payEnv = payEnv,
                                                          appConfig = AppConfig(""),
                                                          clientConfig = jsClientConfig.toClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)
    )

    @JsName("getSubscriptions")
    fun getSubscriptions(token: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getSubscriptions(token)
        }
    }

    @JsName("previewSubscription")
    fun previewSubscription(token: String, planCode: String, addOnCodes: dynamic, couponCode: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            val addOnCodesList = (addOnCodes as Array<String>?)?.toList()
            val previewSubscriptionInfo = PreviewSubscriptionInfo(
                planCode = planCode,
                addOnCodes = addOnCodesList,
                couponCode = couponCode,
            )
            subscriptionService.previewSubscription(token, previewSubscriptionInfo)
        }
    }

    @JsName("purchaseSubscription")
    fun purchaseSubscription(token: String, planCode: String, addOnCodes: dynamic, couponCode: String? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            val addOnCodesList = (addOnCodes as Array<String>?)?.toList()
            subscriptionService.purchaseSubscription(token, planCode, addOnCodesList, couponCode)
        }
    }

    @JsName("reactivateSubscription")
    fun reactivateSubscription(token: String, subscriptionId: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.reactivateSubscription(token, subscriptionId)
        }
    }

    @JsName("cancelSubscription")
    fun cancelSubscription(token: String, subscriptionId: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.cancelSubscription(token, subscriptionId)
        }
    }

    @JsName("getAccountInvoices")
    fun getAccountInvoices(token: String, year: Int?, month: Int?): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getAccountInvoices(token, year, month)
        }
    }

    @JsName("getAccountInvoice")
    fun getAccountInvoice(token: String, invoiceId: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getAccountInvoice(token, invoiceId)
        }
    }

    @JsName("getPlan")
    fun getPlan(planCode: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getPlan(planCode)
        }
    }

    @JsName("getPartnerIdPlans")
    fun getPartnerIdPlans(partnerId: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getPartnerIdPlans(PartnerPlansInfo(partnerId = partnerId))
        }
    }

    @JsName("getPlanUpgrades")
    fun getPlanUpgrades(authToken: String, planCode: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getPlanUpgrades(authToken, planCode)
        }
    }

    @JsName("getV2Invoices")
    fun getV2Invoices(authToken: String, pageNumber: Int? = null, pageSize: Int? = null): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getV2Invoices(authToken, pageNumber, pageSize)
        }
    }

    @JsName("getV2InvoiceById")
    fun getV2InvoiceById(authToken: String, invoiceId: String): Promise<dynamic> {
        return handleJsPromiseRequest(exceptionHandler = ::handleSubscriptionsJsException) {
            subscriptionService.getV2InvoiceById(authToken, invoiceId)
        }
    }

    private fun <T: Any> handleSubscriptionsJsException(ex: Exception, data: JsExceptionInfo<T>? = null): SubscriptionResult.Error {
        return SubscriptionResult.Error(ResponseHeaders.Invalid, Exception(jsRequestExceptionError))
    }
}
